import { Center, Loader, Stack, Table, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconInbox, IconSelector } from '@tabler/icons-react';
import { useMemo, useState } from 'react';

import { contentTypes } from '../../types';

import { BodyRow } from './BodyRow';

type Props = {
  items: contentTypes.Item[];
  pageId?: string;
  managerId?: string;
  isLoading?: boolean;
  emptyMessage?: string;
};

type SortKey = 'name' | 'category' | 'status' | 'date' | 'size';
type SortDirection = 'asc' | 'desc';

const STATUS_ORDER: Record<contentTypes.Status, number> = {
  active: 0,
  inactive: 1,
  archived: 2,
};

const sortableHeaderStyle = { cursor: 'pointer', userSelect: 'none' as const };

const SortIcon = ({
  column,
  sortBy,
  sortDirection,
}: {
  column: SortKey;
  sortBy: SortKey;
  sortDirection: SortDirection;
}) => {
  if (sortBy !== column) return <IconSelector size={14} stroke={1.5} />;
  return sortDirection === 'asc' ? (
    <IconChevronUp size={14} stroke={1.5} />
  ) : (
    <IconChevronDown size={14} stroke={1.5} />
  );
};

const SortableHeader = ({
  column,
  children,
  sortBy,
  sortDirection,
  onToggle,
}: {
  column: SortKey;
  children: string;
  sortBy: SortKey;
  sortDirection: SortDirection;
  onToggle: (key: SortKey) => void;
}) => (
  <Table.Th tt="uppercase" fz="xs" onClick={() => onToggle(column)} style={sortableHeaderStyle}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {children}
      <SortIcon column={column} sortBy={sortBy} sortDirection={sortDirection} />
    </span>
  </Table.Th>
);

export const ContentTable = ({
  items,
  pageId,
  managerId,
  isLoading,
  emptyMessage = 'Нет контента в этой вкладке',
}: Props) => {
  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection(key === 'date' || key === 'size' ? 'desc' : 'asc');
    }
  };

  const sortedItems = useMemo(() => {
    const sorted = [...items];
    const dir = sortDirection === 'asc' ? 1 : -1;

    return sorted.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '', 'ru') * dir;
        case 'category':
          return (a.category || '').localeCompare(b.category || '', 'ru') * dir;
        case 'status':
          return (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) * dir;
        case 'size':
          return (a.size - b.size) * dir;
        case 'date':
        default:
          return (a.createdAt - b.createdAt) * dir;
      }
    });
  }, [items, sortBy, sortDirection]);

  if (isLoading && items.length === 0) {
    return (
      <Center mih={240}>
        <Loader size="sm" />
      </Center>
    );
  }

  if (items.length === 0) {
    return (
      <Center mih={240}>
        <Stack align="center" gap="xs">
          <IconInbox size={40} stroke={1.2} opacity={0.5} />
          <Text c="dimmed" size="sm">
            {emptyMessage}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Table verticalSpacing="md" highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <SortableHeader
            column="name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onToggle={toggleSort}
          >
            Название
          </SortableHeader>
          <SortableHeader
            column="category"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onToggle={toggleSort}
          >
            Категория
          </SortableHeader>
          <SortableHeader
            column="date"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onToggle={toggleSort}
          >
            Дата загрузки
          </SortableHeader>
          <SortableHeader
            column="status"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onToggle={toggleSort}
          >
            Статус
          </SortableHeader>
          <SortableHeader
            column="size"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onToggle={toggleSort}
          >
            Размер
          </SortableHeader>
          <Table.Th tt="uppercase" fz="xs" ta="right">
            Действия
          </Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {sortedItems.map((item) => (
          <BodyRow key={item._id} item={item} pageId={pageId} managerId={managerId} />
        ))}
      </Table.Tbody>
    </Table>
  );
};
